import { CartLineModel, OrderLineModel } from "@insite/client-framework/Types/ApiModels";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";

export interface DataLayerUtilityItem {
    item_id: string;
    item_name: string;
    item_brand?: string;
    item_category?: string;
    item_category2?: string;
    item_category3?: string;
    item_category4?: string;
    item_category5?: string;
    price: number;
    quantity: number;
}

interface DataLayerUtility {
    product: any;
    pricing?: any;
    quantity?: number;
    breadcrumbs?: any[];
    itemObject?: {
        item_list_id: string;
        item_list_name: string;
    } | null | undefined;
    originalQuery?: string;
}

interface Breadcrumb {
    text?: string;
    children?: string;
    url?: string;
    href?: string;
}

export const getDataLayerUtility = ({
    product,
    pricing,
    quantity,
    breadcrumbs = [],
    itemObject = null,
    originalQuery,
}: DataLayerUtility): DataLayerUtilityItem | null => {
    const currency = "USD";
    const path = typeof window === "undefined" ? "" : window.location.pathname;
    if (!product) {
        return null;
    }

    const realQty = quantity || 1;

    const price = pricing
        ? getUnitNetPrice(pricing, realQty).price
        : Number(product.unitListPrice || 0);
        const lowerPathcustom = path.toLowerCase();
    const skipCategories = lowerPathcustom.startsWith("/brands/") || lowerPathcustom.startsWith("/search");

    const categoryProps = skipCategories
    ? {}
    : Object.fromEntries(
          breadcrumbs
              .map((breadcrumb: Breadcrumb) => ({
                  text: breadcrumb.text ?? breadcrumb.children,
                  url: breadcrumb.url ?? breadcrumb.href,
              }))
              .filter(breadcrumb => breadcrumb.text && breadcrumb.text !== "Home")
              .map((breadcrumb, index) => [
                  index === 0 ? "item_category" : `item_category${index + 1}`,
                  breadcrumb.text,
              ]),
      );

    const lowerPath = path.toLowerCase();
    const breadcrumbNames = breadcrumbs
        .map((breadcrumb: Breadcrumb) => (breadcrumb.text ?? breadcrumb.children)?.trim())
        .filter((name: string | undefined): name is string => !!name && name.toLowerCase() !== "home");
    let itemListId: string;
    let itemListName: string;

    if (lowerPath.startsWith("/brands/")) {
        const brandPath = lowerPath.split("/").filter(Boolean)[1];
        const brandValue = product.brand?.name || brandPath;
        const slug = (value: string) => value.toLowerCase().trim().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
        const fallbackBrandName = brandPath
            ? brandPath.replace(/[-_]+/g, " ").replace(/\b\w/g, (character: string) => character.toUpperCase())
            : "";

        itemListId = brandValue ? `brand_${slug(brandValue)}` : "brand";
        itemListName = `Brand: ${product.brand?.name || fallbackBrandName}`;
    } else if (lowerPath.startsWith("/search")) {
        itemListId = "search_results";
        itemListName = `Search: ${originalQuery}`;
    } else {
        const categoryName = breadcrumbNames[breadcrumbNames.length - 1];
        const categoryPath = path.split("/").filter(Boolean).pop();
        const fallbackCategoryName = categoryPath
            ? categoryPath.replace(/[-_]+/g, " ").replace(/\b\w/g, character => character.toUpperCase())
            : "";
        const categoryValue = categoryName || categoryPath;

        itemListId = categoryValue
            ? `category_${categoryValue.toLowerCase().trim().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "")}`
            : "product_list";
        itemListName = `Category: ${categoryName || fallbackCategoryName}`;
    }

    if (itemObject !== null && itemObject !== undefined) {
        itemListId = itemObject.item_list_id;
        itemListName = itemObject.item_list_name;
    }

    const dataLayerObject = {
        item_id: product.productNumber || product.manufacturerItem,
        item_name: product.productTitle || product.content?.pageTitle || "",
        item_brand: product.brand?.name,
        price,
        quantity: realQty,
        ...categoryProps,
    }

    if (typeof window !== "undefined") {
        const dataLayerWindow = window as any;
        dataLayerWindow.dataLayer = dataLayerWindow.dataLayer || [];
        dataLayerWindow.dataLayer.push({ ecommerce: null });
        dataLayerWindow.dataLayer.push({
            event: "select_item",
            ecommerce: {
                currency,
                value: Number((price * realQty).toFixed(2)),
                items: [dataLayerObject],
                item_list_id: itemListId,
                item_list_name: itemListName,
            },
        });
    }

    return dataLayerObject
};


export interface DataLayerUtilityItem {
    item_id: string;
    item_name: string;
    item_brand?: string;
    item_category?: string;
    item_category2?: string;
    item_category3?: string;
    item_category4?: string;
    item_category5?: string;
    price: number;
    quantity: number;
}



export const getDataLayerUtilityV2 = (eventName: string, cartLines: CartLineModel[] | null | undefined, itemListObj?: any) => {
    if (!cartLines?.length) {
        return;
    }

    const items: DataLayerUtilityItem[] = cartLines.map(cartLine => {
        const quantity = cartLine.qtyOrdered ?? 1;
        const price = cartLine.pricing?.unitNetPrice ?? 0;
        const categories = (cartLine.properties?.categoryStructure || "")
            .split(":")
            .map(category => category.trim())
            .filter(Boolean)
            .slice(0, 5);
        const categoryProps = categories.reduce<Record<string, string>>((properties, category, index) => {
            properties[index === 0 ? "item_category" : `item_category${index + 1}`] = category;
            return properties;
        }, {});

        return {
            item_id: cartLine.erpNumber || cartLine.manufacturerItem || cartLine.productId || "",
            item_name: cartLine.shortDescription || cartLine.productName,
            item_brand: cartLine.brand?.name || "",
            price,
            quantity,
            ...categoryProps,
            ...itemListObj
        };
    });

    const value = items.reduce((total, item) => total + item.price * item.quantity, 0);
    const ecommerce = {
        currency: "USD",
        value: Number(value.toFixed(2)),
        items,
    };

    if (typeof window !== "undefined") {
        const dataLayerWindow = window as any;
        dataLayerWindow.dataLayer = dataLayerWindow.dataLayer || [];
        dataLayerWindow.dataLayer.push({ ecommerce: null });
        dataLayerWindow.dataLayer.push({
            event: eventName,
            ecommerce,
        });
    }

    return ecommerce;
};

// Order Details Page - Order Lines List - Product Click
export const getDataLayerUtilityV3 = (eventName: string, orderLines: OrderLineModel[], itemListObj?: any) => {
    if (!orderLines?.length) {
        return;
    }

    const items: DataLayerUtilityItem[] = orderLines.map(orderLine => {
        const quantity = orderLine.qtyOrdered ?? 1;
        const price = orderLine?.unitNetPrice ?? 0;

        return {
            item_id: orderLine.productErpNumber || orderLine.manufacturerItem || orderLine.productId || "",
            item_name: orderLine.shortDescription || orderLine.description,
            item_brand: orderLine.brand?.name || "",
            price,
            quantity,
            ...itemListObj
        };
    });

    const value = items.reduce((total, item) => total + item.price * item.quantity, 0);
    const ecommerce = {
        currency: "USD",
        value: Number(value.toFixed(2)),
        items,
    };

    if (typeof window !== "undefined") {
        const dataLayerWindow = window as any;
        dataLayerWindow.dataLayer = dataLayerWindow.dataLayer || [];
        dataLayerWindow.dataLayer.push({ ecommerce: null });
        dataLayerWindow.dataLayer.push({
            event: eventName,
            ecommerce,
        });
    }

    return ecommerce;
};


// My Account Page - Product Click
// This is for the My Account Page - My Lists Details Page - Product Click
export interface DataLayerEcommerce {
    currency: string;
    value?: number;
    items: any[];
}
export const getDataLayerUtilityV4 = (dataLayerObject: any): DataLayerEcommerce | undefined => {
    const rawItems: any[] = Array.isArray(dataLayerObject)
        ? dataLayerObject
        : dataLayerObject
            ? [dataLayerObject]
            : [];

    if (!rawItems.length) {
        return;
    }

    // Take `value` out of the item(s) so it is sent at the ecommerce level.
    let value: number | undefined;
    const items = rawItems.map(rawItem => {
        const { value: itemValue, ...item } = rawItem;
        if (value === undefined && itemValue !== undefined && itemValue !== null) {
            value = Number(itemValue);
        }
        return item;
    });

    const ecommerce: DataLayerEcommerce = {
        currency: "USD",
        ...(value !== undefined ? { value } : {}),
        items,
    };

    if (typeof window !== "undefined") {
        const dataLayerWindow = window as any;
        dataLayerWindow.dataLayer = dataLayerWindow.dataLayer || [];
        dataLayerWindow.dataLayer.push({ ecommerce: null });
        dataLayerWindow.dataLayer.push({
            event: "select_item",
            ecommerce,
        });
    }

    return ecommerce;
};
